import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// INTERFACES
import { IPagination } from '../../../interfaces';
import { IWarehouseGroup } from '../../../interfaces/location-warehouse.interface';

// SERVICES
import { EnvironmentUtilService } from '../../util';
import { ApiService } from '../api.service';

@Injectable({
  providedIn: 'root',
})
export class LocationWarehouseGroupApiService {
  private readonly _warehouseGroups = new BehaviorSubject<IWarehouseGroup[]>(
    <IWarehouseGroup[]>[],
  );
  private readonly _pagination = new BehaviorSubject<IPagination>(<IPagination>{});
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  private readonly _submittingForm = new BehaviorSubject<boolean>(<boolean>false);

  warehouseGroups$ = this._warehouseGroups.asObservable();
  loading$ = this._loading.asObservable();
  pagination$ = this._pagination.asObservable();
  submittingForm$ = this._submittingForm.asObservable();

  environment = this.environmentUtilService.getEnvironment();

  constructor(
    private apiService :ApiService,
    private environmentUtilService :EnvironmentUtilService,
  ) {}

  //TODO: DEPRECATED
  get(params :{ [param :string] :string | string[] }) :Subscription {
    this._loading.next(true);

    return this.apiService
      .get(`${this.environment.urlV2}/location/warehouse-groups`, {
        params,
      })
      .subscribe({
        next: (response) => {
          this._warehouseGroups.next(response.data);
          this._pagination.next(response.meta);
          this._loading.next(false);
        },
        error: (error) => {
          this._loading.next(false);

          return throwError(() => new Error(error));
        },
      });
  }

  getV2(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/location/warehouse-groups`,
      { params },
    );
  }

  getById(id :number) :Observable<IWarehouseGroup> {
    return this.apiService.get(
      `${this.environment.urlV2}/location/warehouse-groups/${id}`,
      {},
    );
  }

  create(payload :{
    label :string;
    description :string;
    warehouseIds :number[];
    relations ?:string;
  }) :Observable<any> {
    this._submittingForm.next(true);

    return this.apiService
      .post({
        path: `${this.environment.urlV2}/location/warehouse-groups`,
        body: payload,
      })
      .pipe(
        map((response) => {
          this._warehouseGroups.next([
            ...this._warehouseGroups.getValue(),
            response,
          ]);

          this._submittingForm.next(false);

          return response;
        }),
        catchError((response) => {
          this._submittingForm.next(false);

          return throwError(response.error);
        }),
      );
  }

  update(
    id :number,
    payload :{
      label :string;
      description :string;
      warehouseIds :number[];
      relations ?:string;
    },
  ) :Observable<any> {
    this._submittingForm.next(true);

    return this.apiService
      .put(`${this.environment.urlV2}/location/warehouse-groups/${id}`, payload)
      .pipe(
        map((response :any) => {
          const warehouseGroups = this._warehouseGroups.getValue();
          const updatedIndex = warehouseGroups.findIndex(
            (warehouseGroup) => warehouseGroup.id === response.id,
          );

          if (updatedIndex !== -1) {
            warehouseGroups.splice(updatedIndex, 1, response);
            this._warehouseGroups.next(warehouseGroups);
          }

          this._submittingForm.next(false);

          return response;
        }),
        catchError((response) => {
          this._submittingForm.next(false);

          return throwError(response.error);
        }),
      );
  }

  delete(id :number) {
    return this.apiService
      .delete(`${this.environment.urlV2}/location/warehouse-groups/${id}`)
      .pipe(
        map((response) => response),
        catchError((response) => {
          this._submittingForm.next(false);

          return throwError(response.error);
        }),
      );
  }

  getWarehouseGroups(params ?:{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/location/warehouse-groups`,
      { params },
    );
  }

  updatePushOrderSettings(id :number) :Observable<any> {
    return this.apiService.put(
      `${this.environment.urlV2}/location/warehouse-groups/${id}/update-push-order-settings`,
    );
  }

  updateInventoryNumbersSettings(id :number) :Observable<any> {
    return this.apiService.put(
      `${this.environment.urlV2}/location/warehouse-groups/${id}/update-inventory-numbers-settings`,
    );
  }

  getDefaultWarehouseGroup(params ?:{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.apiService.get(
      `${this.environment.urlV2}/location/warehouse-groups/default`,
      { params },
    );
  }
}
